// Carry Roll Public simulate entry — PSD §4.2. Single orchestrator assembling FlightResult.
import { aggregate } from './aggregate';
import { buildExplanation, confidence } from './explain';
import { classifyProfile, classifyShape } from './classify';
import { deriveDelivery } from './delivery';
import { simulateFlight } from './flight';
import { simulateRoll } from './roll';
import { validate } from './validate';
import { Fidelity } from './types';
/**
 * Deduplicate warnings so each distinct (field, code) surfaces exactly once.
 * validate() is the authoritative owner of range/missing/implausible warnings;
 * downstream stages (flight, roll) may re-emit the same field+code with a
 * model-specific message (e.g. the low-launch-angle clamp). Keep the first
 * (most descriptive) occurrence and drop subsequent duplicates so the
 * ValidationErrors panel never shows the same caution twice.
 */
function dedupeWarnings(warnings) {
    const seen = new Set();
    const out = [];
    for (const w of warnings) {
        const key = `${w.field}:${w.code}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(w);
    }
    return out;
}
/**
 * Single public entry. Orchestrates validate → airDensity → simulateFlight →
 * simulateRoll → aggregate → measuredDistance → classify → explain.
 * Never throws on valid input; clamps + warns otherwise.
 */
export function simulate(req) {
    const warnings = [...validate(req)];
    // Apply delivery-derived shifts (3.1–3.6) to produce an effective request.
    const derived = deriveDelivery(req.delivery, req.launch);
    const effectiveReq = {
        ...req,
        launch: {
            ...req.launch,
            ballSpeed: req.launch.ballSpeed * derived.ballSpeedFactor,
            launchAngle: derived.launch.launchAngle ?? req.launch.launchAngle,
            spinRpm: derived.launch.spinRpm ?? req.launch.spinRpm,
            spinAxisDeg: req.launch.spinAxisDeg + derived.spinAxisShiftDeg,
            launchDirDeg: req.launch.launchDirDeg + derived.lateralShiftDeg,
        },
    };
    const flight = simulateFlight(effectiveReq);
    warnings.push(...flight.warnings);
    const roll = simulateRoll({
        carryM: flight.carryM,
        landingAngleDeg: flight.landingAngleDeg,
        spinRpm: effectiveReq.launch.spinRpm,
        env: req.environment,
        club: req.club,
        vxLand: flight.vxLand,
        zLand: flight.zLand,
        override: req.override,
    });
    warnings.push(...roll.warnings);
    const agg = aggregate({ carryM: flight.carryM, rollM: roll.rollM, measuredM: 0 });
    // Measured distance: re-run flight at sea-level, no wind, no elevation.
    const measuredReq = {
        ...req,
        environment: {
            ...req.environment,
            altitudeM: 0,
            windSpeed: 0,
            elevationDiffM: 0,
            seaLevelBaseline: false,
        },
        fidelity: Fidelity.Full,
    };
    const measuredFlight = simulateFlight(measuredReq);
    const measuredRoll = simulateRoll({
        carryM: measuredFlight.carryM,
        landingAngleDeg: measuredFlight.landingAngleDeg,
        spinRpm: req.launch.spinRpm,
        env: measuredReq.environment,
        club: req.club,
        vxLand: measuredFlight.vxLand,
        zLand: measuredFlight.zLand,
        override: req.override,
    });
    const measuredM = measuredFlight.carryM + measuredRoll.rollM;
    // Shape: P1 spin axis defaults 0 → straight. Total curvature from spin axis tilt.
    const totalCurvatureDeg = req.launch.spinAxisDeg;
    const { shape } = classifyShape(req.launch.spinAxisDeg, totalCurvatureDeg);
    const { profile } = classifyProfile(flight.apexM, flight.carryM);
    const result = {
        carryM: flight.carryM,
        rollM: roll.rollM,
        totalM: agg.totalM,
        rollPercent: agg.rollPercent,
        measuredM,
        playingM: agg.playingM,
        apexM: flight.apexM,
        landingAngleDeg: flight.landingAngleDeg,
        shotShape: shape,
        profile,
        offlineM: roll.offlineM,
        flightPath: flight.flightPath,
        rollPath: roll.rollPath,
        confidence: confidence(req),
        explanation: '',
        warnings: dedupeWarnings(warnings),
    };
    result.explanation = buildExplanation(req, result);
    return result;
}
//# sourceMappingURL=simulate.js.map